// Stakeholder map hooks — FE-SCRM-013.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  addStakeholder,
  deleteStakeholder,
  fetchProspectContacts,
  fetchStakeholderMap,
  fetchStakeholderRisks,
  updateStakeholder,
} from "./api";
import type {
  AddStakeholderInput,
  SalesProspectContactsResponse,
  StakeholderMapResponse,
  StakeholderRiskListResponse,
  StakeholderRow,
  UpdateStakeholderInput,
} from "./types";


export const STAKEHOLDER_MAP_KEY = "sales-stakeholder-map" as const;
export const STAKEHOLDER_RISKS_KEY = "sales-stakeholder-risks" as const;
export const PROSPECT_CONTACTS_KEY = "sales-prospect-contacts" as const;


export function useStakeholderMapQuery(
  opportunityId: string | undefined,
): UseQueryResult<StakeholderMapResponse> {
  return useQuery({
    queryKey: [STAKEHOLDER_MAP_KEY, opportunityId],
    queryFn: ({ signal }) => fetchStakeholderMap(opportunityId!, { signal }),
    enabled: Boolean(opportunityId),
  });
}


export function useStakeholderRisksQuery(
  filters?: { producerId?: string; stage?: string },
): UseQueryResult<StakeholderRiskListResponse> {
  return useQuery({
    queryKey: [STAKEHOLDER_RISKS_KEY, filters?.producerId, filters?.stage],
    queryFn: ({ signal }) =>
      fetchStakeholderRisks({
        signal, producerId: filters?.producerId, stage: filters?.stage,
      }),
  });
}


export function useProspectContactsQuery(
  prospectId: string | undefined,
): UseQueryResult<SalesProspectContactsResponse> {
  return useQuery({
    queryKey: [PROSPECT_CONTACTS_KEY, prospectId],
    queryFn: ({ signal }) =>
      fetchProspectContacts(prospectId!, { signal }),
    enabled: Boolean(prospectId),
  });
}


function _invalidate(
  qc: ReturnType<typeof useQueryClient>, opportunityId: string,
) {
  qc.invalidateQueries({ queryKey: [STAKEHOLDER_MAP_KEY, opportunityId] });
  qc.invalidateQueries({ queryKey: [STAKEHOLDER_RISKS_KEY] });
}


export function useAddStakeholderMutation(
  opportunityId: string,
): UseMutationResult<StakeholderRow, Error, AddStakeholderInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => addStakeholder(opportunityId, input),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}


export interface UpdateStakeholderVariables {
  role_id: string;
  values: UpdateStakeholderInput;
}


export function useUpdateStakeholderMutation(
  opportunityId: string,
): UseMutationResult<StakeholderRow, Error, UpdateStakeholderVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateStakeholder(vars.role_id, vars.values),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}


export function useDeleteStakeholderMutation(
  opportunityId: string,
): UseMutationResult<{ deleted: boolean }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => deleteStakeholder(roleId),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}
